<script>
import constants from '@/constants';
import { formatMinskDate } from '@/scripts/tools.js';

const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
const SLOW_REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
const { IDLE, REFRESHING, FAILED } = constants.REFRESH_STATUS;

export default {
    props: {
        refreshStatus: {
            type: Object,
            required: true,
        },
        isRefreshing: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pollInterval: null,
            currentTime: Date.now(),
            timeUpdateInterval: null,
        };
    },
    computed: {
        isStale() {
            if (this.refreshStatus.status !== REFRESHING) return false;
            if (!this.refreshStatus.updatedAt) return false;

            const updatedAt = new Date(this.refreshStatus.updatedAt).getTime();
            const elapsed = this.currentTime - updatedAt;
            return elapsed > STALE_THRESHOLD_MS;
        },
        isSlowRefresh() {
            if (this.refreshStatus.status !== REFRESHING) return false;
            if (!this.refreshStatus.updatedAt) return false;

            const updatedAt = new Date(this.refreshStatus.updatedAt).getTime();
            const elapsed = this.currentTime - updatedAt;
            return (
                elapsed > SLOW_REFRESH_THRESHOLD_MS &&
                elapsed <= STALE_THRESHOLD_MS
            );
        },
        isFailed() {
            return this.refreshStatus.status === FAILED;
        },
        isButtonDisabled() {
            return this.isRefreshing && !this.isStale;
        },
        buttonText() {
            if (this.isStale || this.isFailed) return 'Retry Refresh';
            if (this.isRefreshing) return 'Refreshing...';
            return 'Refresh Data';
        },
        buttonClass() {
            if (this.isStale || this.isFailed)
                return 'refresh_button button_warning';
            return 'refresh_button';
        },
        statusClass() {
            if (this.isStale) {
                return { status_stale: true };
            }
            return {
                status_idle: this.refreshStatus.status === IDLE,
                status_refreshing: this.refreshStatus.status === REFRESHING,
                status_failed: this.refreshStatus.status === FAILED,
            };
        },
        statusLabel() {
            if (this.isStale) return 'Refresh appears stuck';
            const labels = {
                [IDLE]: 'Data is up to date',
                [REFRESHING]: 'Refresh in progress...',
                [FAILED]: 'Last refresh failed',
            };
            return labels[this.refreshStatus.status] || 'Unknown';
        },
        formattedLastRefresh() {
            if (!this.refreshStatus.lastRefreshAt) return 'Never';
            return formatMinskDate(this.refreshStatus.lastRefreshAt, true);
        },
        formattedDuration() {
            const seconds = this.refreshStatus.refreshDurationSeconds;
            if (seconds === null || seconds === undefined) return 'N/A';
            return `${seconds.toFixed(2)}s`;
        },
    },
    watch: {
        isRefreshing(newVal) {
            if (newVal) {
                this.startPolling();
            } else {
                this.stopPolling();
            }
        },
    },
    mounted() {
        this.$store.dispatch('loadRefreshStatus');
        if (this.isRefreshing) {
            this.startPolling();
        }
        // Update current time every 10 seconds for stale detection
        this.timeUpdateInterval = setInterval(() => {
            this.currentTime = Date.now();
        }, 10000);
    },
    beforeUnmount() {
        this.stopPolling();
        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval);
            this.timeUpdateInterval = null;
        }
    },
    methods: {
        async triggerRefresh() {
            const response = await this.$store.dispatch('triggerRefresh');
            if (response) {
                this.startPolling();
            }
        },
        startPolling() {
            this.stopPolling();
            this.pollInterval = setInterval(() => {
                this.$store.dispatch('loadRefreshStatus');
            }, 5000);
        },
        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        },
    },
};
</script>

<template>
    <div class="refresh_card">
        <div class="refresh_header">
            <span class="refresh_title">Data Refresh</span>
            <span class="auto_refresh_note">Auto-refreshes hourly</span>
        </div>

        <div class="refresh_info">
            <div class="info_row">
                <span class="info_label">Status:</span>
                <span class="status_indicator" :class="statusClass">
                    <span
                        v-if="isRefreshing && !isStale"
                        class="spinner"
                    ></span>
                    {{ statusLabel }}
                </span>
            </div>

            <div class="info_row">
                <span class="info_label">Last Refresh:</span>
                <span class="info_value">{{ formattedLastRefresh }}</span>
            </div>

            <div v-if="refreshStatus.refreshDurationSeconds" class="info_row">
                <span class="info_label">Duration:</span>
                <span class="info_value">{{ formattedDuration }}</span>
            </div>

            <!-- Slow refresh warning -->
            <div v-if="isSlowRefresh" class="warning_message">
                ⏳ Refresh is taking longer than usual. Please wait...
            </div>

            <!-- Stale refresh warning -->
            <div v-if="isStale" class="warning_message warning_stale">
                ⚠️ Refresh appears to be stuck (>10 min). Click "Retry Refresh"
                to attempt recovery.
            </div>

            <!-- Error message from backend -->
            <div v-if="refreshStatus.errorMessage" class="error_message">
                {{ refreshStatus.errorMessage }}
            </div>
        </div>

        <button
            :class="buttonClass"
            @click="triggerRefresh"
            :disabled="isButtonDisabled"
        >
            {{ buttonText }}
        </button>
    </div>
</template>

<style scoped>
.refresh_card {
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
}

.refresh_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.refresh_title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color);
}

.auto_refresh_note {
    font-size: 11px;
    color: var(--text-color-secondary);
}

.refresh_info {
    margin-bottom: 12px;
}

.info_row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.info_label {
    font-size: 13px;
    color: var(--text-color-secondary);
    min-width: 80px;
}

.info_value {
    font-size: 13px;
    color: var(--text-color);
}

.status_indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    padding: 2px 8px;
    border-radius: 4px;
}

.status_idle {
    background: #d1fae5;
    color: #065f46;
}

.status_refreshing {
    background: #dbeafe;
    color: #1e40af;
}

.status_failed {
    background: #fee2e2;
    color: #991b1b;
}

.status_stale {
    background: #fef3c7;
    color: #92400e;
}

.spinner {
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.warning_message {
    margin-top: 8px;
    padding: 8px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 4px;
    font-size: 12px;
}

.warning_stale {
    background: #fed7aa;
    color: #c2410c;
}

.error_message {
    margin-top: 8px;
    padding: 8px;
    background: #fee2e2;
    color: #991b1b;
    border-radius: 4px;
    font-size: 12px;
}

.refresh_button {
    width: 100%;
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s ease;
}

.refresh_button:hover:not(:disabled) {
    background: #2563eb;
}

.refresh_button:disabled {
    background: #94a3b8;
    color: #1e293b;
    cursor: not-allowed;
}

.button_warning {
    background: #f59e0b;
    color: white;
}

.button_warning:hover:not(:disabled) {
    background: #d97706;
    opacity: 1;
}
</style>
